import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validation utilities (same as AppointmentForm)
const validators = {
  sanitizeInput: (input) => {
    if (!input) return '';
    // Do NOT trim here — runs on every keystroke; trimming eats spaces
    // between words. Trim once at submit instead.
    return input
      .replace(/<[^>]*>/g, '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  },

  containsHTML: (input) => {
    if (!input) return false;
    const htmlPattern = /<[a-z][\s\S]*>/i;
    const scriptPattern = /<script|javascript:|onclick|onerror|onload/i;
    return htmlPattern.test(input) || scriptPattern.test(input);
  },

  isValidPhone: (phone) => {
    if (!phone) return false;
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  },

  isValidEmail: (email) => {
    if (!email) return false;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }
};

const ConsultationForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    planning_timeline: '',
    referral_source: '',
    financial_assistance: '',
    contact_method: 'email',
    message: '',
    // Honeypot field for bot detection (should remain empty)
    website: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formStartTime] = useState(Date.now());

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    const sanitizedValue = validators.sanitizeInput(value);
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
  }, []);

  const handlePhoneChange = useCallback((e) => {
    const { value } = e.target;
    const cleaned = value.replace(/[^\d\s\-()]/g, '');
    setFieldErrors(prev => ({ ...prev, phone: '' }));
    setFormData(prev => ({ ...prev, phone: cleaned }));
  }, []);

  const validateForm = () => {
    const errors = {};

    // Check for HTML in text fields
    if (validators.containsHTML(formData.name) || validators.containsHTML(formData.message)) {
      errors.name = 'Invalid characters detected. Please remove any HTML or special formatting.';
    }

    // Validate required fields
    if (!formData.name.trim()) errors.name = 'Name is required.';
    if (!validators.isValidPhone(formData.phone)) errors.phone = 'Please enter a valid 10-digit phone number.';
    if (!validators.isValidEmail(formData.email)) errors.email = 'Please enter a valid email address.';
    if (!formData.planning_timeline) errors.planning_timeline = 'Please select a timeline.';
    if (!formData.referral_source) errors.referral_source = 'Please select how you heard about us.';
    if (!formData.financial_assistance) errors.financial_assistance = 'Please select yes or no.';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bot detection: Honeypot check
    if (formData.website) {
      // Bot filled in the honeypot field - silently reject
      setSuccess(true);
      return;
    }

    // Bot detection: Time-based check (form filled too quickly)
    const timeTaken = Date.now() - formStartTime;
    if (timeTaken < 3000) { // Less than 3 seconds
      setSuccess(true);
      return;
    }

    if (!validateForm()) {
      setError('Please correct the errors above.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const submitData = {
        name: validators.sanitizeInput(formData.name).trim(),
        phone: formData.phone.replace(/\D/g, ''),
        email: formData.email,
        planning_timeline: formData.planning_timeline,
        referral_source: formData.referral_source,
        financial_assistance: formData.financial_assistance,
        contact_method: formData.contact_method,
        message: validators.sanitizeInput(formData.message),
        source: 'dental_implant_center' // Identifies this form
      };

      await axios.post(`${API}/consultations`, submitData);
      setSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        planning_timeline: '',
        referral_source: '',
        financial_assistance: '',
        contact_method: 'email',
        message: '',
        website: ''
      });
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to submit. Please try again or call us directly.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg" id="reqestfreeconsult">
      <h2 className="text-3xl font-bold text-gray-800 mb-2">Request FREE Implant Consultation</h2>
      <p className="text-gray-600 mb-6">Fill out the form below and we'll contact you to schedule your free dental implant consultation.</p>
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          Thank you! Your implant consultation request has been submitted. We'll contact you soon.
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Honeypot field - hidden from users, bots will fill it */}
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
          <label htmlFor="website">Leave this field empty</label>
          <input
            type="text"
            name="website"
            id="website"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        {/* Row 1: Name and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              className={`mt-1 ${fieldErrors.name ? 'border-red-500' : ''}`}
              maxLength={100}
            />
            {fieldErrors.name && <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>}
          </div>
          <div>
            <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="(817) 555-1234"
              className={`mt-1 ${fieldErrors.phone ? 'border-red-500' : ''}`}
              maxLength={20}
            />
            {fieldErrors.phone && <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>}
          </div>
        </div>

        {/* Row 2: Email */}
        <div>
          <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            className={`mt-1 ${fieldErrors.email ? 'border-red-500' : ''}`}
            maxLength={100}
          />
          {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>}
        </div>

        {/* Row 3: Timeline */}
        <div>
          <Label>Planning on getting implants <span className="text-red-500">*</span></Label>
          <Select
            value={formData.planning_timeline}
            onValueChange={(value) => {
              setFieldErrors(prev => ({ ...prev, planning_timeline: '' }));
              setFormData({ ...formData, planning_timeline: value });
            }}
          >
            <SelectTrigger className={`mt-1 ${fieldErrors.planning_timeline ? 'border-red-500' : ''}`}>
              <SelectValue placeholder="Select timeline..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ASAP">ASAP</SelectItem>
              <SelectItem value="Within the next month">Within the next month</SelectItem>
              <SelectItem value="Within 6 Months">Within 6 Months</SelectItem>
              <SelectItem value="Just doing research">Just doing research</SelectItem>
            </SelectContent>
          </Select>
          {fieldErrors.planning_timeline && <p className="text-red-500 text-xs mt-1">{fieldErrors.planning_timeline}</p>}
        </div>

        {/* Row 4: How did you hear */}
        <div>
          <Label>How did you hear about us? <span className="text-red-500">*</span></Label>
          <Select
            value={formData.referral_source}
            onValueChange={(value) => {
              setFieldErrors(prev => ({ ...prev, referral_source: '' }));
              setFormData({ ...formData, referral_source: value });
            }}
          >
            <SelectTrigger className={`mt-1 ${fieldErrors.referral_source ? 'border-red-500' : ''}`}>
              <SelectValue placeholder="Select one..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Search Engine">Search Engine</SelectItem>
              <SelectItem value="Family/Friend">Family/Friend</SelectItem>
              <SelectItem value="Promotion">Promotion</SelectItem>
              <SelectItem value="Social Media">Social Media</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          {fieldErrors.referral_source && <p className="text-red-500 text-xs mt-1">{fieldErrors.referral_source}</p>}
        </div>

        {/* Row 5: Financial Assistance */}
        <div>
          <Label>Will you need financial assistance? <span className="text-red-500">*</span></Label>
          <RadioGroup
            value={formData.financial_assistance}
            onValueChange={(value) => {
              setFieldErrors(prev => ({ ...prev, financial_assistance: '' }));
              setFormData({ ...formData, financial_assistance: value });
            }}
            className="flex space-x-6 mt-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Yes" id="financial-yes" />
              <Label htmlFor="financial-yes" className="font-normal cursor-pointer">Yes</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="No" id="financial-no" />
              <Label htmlFor="financial-no" className="font-normal cursor-pointer">No</Label>
            </div>
          </RadioGroup>
          {fieldErrors.financial_assistance && <p className="text-red-500 text-xs mt-1">{fieldErrors.financial_assistance}</p>}
        </div>

        {/* Row 6: Contact Method */}
        <div>
          <Label>Preferred contact method</Label>
          <RadioGroup
            value={formData.contact_method}
            onValueChange={(value) => setFormData({ ...formData, contact_method: value })}
            className="flex flex-wrap gap-4 mt-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="email" id="implant-contact-email" />
              <Label htmlFor="implant-contact-email" className="font-normal cursor-pointer">Email</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="call" id="implant-contact-call" />
              <Label htmlFor="implant-contact-call" className="font-normal cursor-pointer">Phone Call</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="text" id="implant-contact-text" />
              <Label htmlFor="implant-contact-text" className="font-normal cursor-pointer">Text Message</Label>
            </div>
          </RadioGroup>
          
          {/* 10DLC Compliance Notice */}
          {formData.contact_method === 'text' && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <strong>SMS Consent Notice:</strong> By selecting text message, you consent to receive SMS messages from Fort Worth Dental Implant Center regarding your consultation. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance.
            </div>
          )}
        </div>

        {/* Row 7: Message (optional) */}
        <div>
          <Label htmlFor="message">Additional Information (optional)</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your dental implant needs..."
            rows={3}
            className="mt-1"
            maxLength={1000}
          />
          <p className="text-xs text-gray-500 mt-1">{formData.message.length}/1000 characters</p>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3"
          data-testid="consultation-form-submit"
        >
          {loading ? 'Submitting...' : 'Request Free Implant Consultation'}
        </Button>

        <p className="text-xs text-gray-500 text-center">
          Your information is secure and will never be shared with third parties.
        </p>
      </form>
    </div>
  );
};

export default ConsultationForm;
